import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BadgePercent, ChevronRight, Filter, Mail, Menu, MessageCircle, Phone, Plus, Send, Sparkles, X } from "lucide-react";
import axios from "axios";
import Lenis from "lenis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1740209475472-aa7d280f7452?auto=format&fit=crop&w=2000&q=85";
const WHATSAPP = "918667358581";
const imgUrl = (u) => !u ? HERO_IMG : (u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);
const PHONE_PRIMARY = "+91 86673 58581";
const PHONE_SECONDARY = "+91 93443 38581";
const EMAIL = "kubeilaexim@gmail.com";
const categories = ["ALL", "CNC INSERTS", "VMC TOOLING", "END MILLS", "DRILLS"];

export function Logo({ compact = false }) {
  return <div className={compact ? "logo compact" : "logo"} data-testid="brand-logo"><span className="logo-mark">K</span><span><b>KUBEILA</b><small>MATERIALS EXIM</small></span></div>;
}

function Reveal({ children, delay = 0, className = "" }) {
  return <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: .7, delay, ease: [.22, 1, .36, 1] }}>{children}</motion.div>;
}

function ProductCard({ p, addQuote }) {
  const allImgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])).map(imgUrl);
  const [idx, setIdx] = useState(0);
  // front card photo honours the admin's "show on card" choice; gallery keeps all photos
  const showFront = p.show_image !== false && allImgs.length > 0;
  const main = showFront ? (allImgs[idx] || allImgs[0]) : "";
  return (
    <article className={`product-card ${p.offer ? "has-offer" : ""}`} data-testid={`product-card-${p.id}`}>
      <Link to={`/product/${p.id}`} className="product-photo" data-testid={`product-photo-link-${p.id}`} aria-label={`View ${p.name}`}>
        {main ? <img src={main} alt={p.name} loading="lazy"/> : <div className="photo-placeholder-card" data-testid={`photo-placeholder-${p.id}`}><span className="logo-mark">K</span></div>}
        <span>{p.category}</span>
        {p.offer && <span className="offer-badge" data-testid={`offer-badge-${p.id}`}><BadgePercent size={12}/> {p.offer}</span>}
      </Link>
      <button className="card-add-btn" onClick={() => addQuote(p)} aria-label={`Add ${p.name} to quote`} data-testid={`add-${p.id}-button`}><Plus size={19}/></button>
      {allImgs.length > 1 && showFront && (
        <div className="photo-thumbs" data-testid={`thumbs-${p.id}`}>
          {allImgs.map((u, i) => (
            <button key={u} className={i === idx ? "active" : ""} onClick={() => setIdx(i)} aria-label={`View image ${i + 1} of ${p.name}`} data-testid={`thumb-${p.id}-${i}`}>
              <img src={u} alt="" loading="lazy"/>
            </button>
          ))}
        </div>
      )}
      <div className="product-info">
        <div><h3><Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`}>{p.name}</Link></h3><p>{p.grade}{p.grade && p.coating ? " · " : ""}{p.coating}</p></div>
        <b>{p.price}</b>
      </div>
      {p.sizes && p.sizes.length > 0 && (
        <div className="sizes" data-testid={`sizes-${p.id}`}>
          <span>AVAILABLE SIZES</span>
          <div className="size-chips">{p.sizes.map(s => <i key={s}>{s}</i>)}</div>
        </div>
      )}
      <div className="specs">
        <span>DIMENSIONS <strong>{p.dims || "—"}</strong></span>
        <span>APPLICATION <strong>{p.app || "—"}</strong></span>
        <span>STOCK <strong data-testid={`stock-${p.id}`}>{p.stock || "Available"}</strong></span>
      </div>
    </article>
  );
}

function Home() {
  const [active, setActive] = useState("ALL");
  const [quote, setQuote] = useState([]);
  const [modal, setModal] = useState(false);
  const [sent, setSent] = useState("");
  const [mobile, setMobile] = useState(false);
  const [products, setProducts] = useState([]);

  const filtered = useMemo(() => active === "ALL" ? products : products.filter(p => p.category === active), [active, products]);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  const addQuote = (product) => setQuote(q => q.find(x => x.id === product.id) ? q : [...q, product]);

  const submitQuote = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    try {
      const r = await axios.post(`${API}/quotes`, { name: data.get("name"), company: data.get("company"), email: data.get("email"), phone: data.get("phone"), message: data.get("message"), products: quote.map(p => p.name) });
      setSent(`Reference ${r.data.reference} — we will be in touch.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : (typeof detail === "string" ? detail : "Please check your details and try again.");
      setSent(msg);
    }
  };

  useEffect(() => {
    document.title = "Kubeila — Precision, cut in";
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  return <div className="site-shell">
    <nav className="nav">
      <a href="#top" aria-label="Kubeila home"><Logo /></a>
      <div className={`nav-links ${mobile ? "open" : ""}`}>
        <a href="#catalog" data-testid="nav-catalog-link">Catalogue</a>
        <a href="#capabilities" data-testid="nav-capabilities-link">Capabilities</a>
        <a href="#contact" data-testid="nav-contact-link">Contact</a>
        <button className="nav-cta" onClick={() => setModal(true)} data-testid="nav-quote-button">Build a quote <ArrowUpRight size={15}/></button>
      </div>
      <button className="menu-btn" onClick={() => setMobile(!mobile)} aria-label="Toggle menu" data-testid="mobile-menu-button">{mobile ? <X/> : <Menu/>}</button>
    </nav>

    <main id="top">
      <section className="hero">
        <div className="hero-grid"/>
        <div className="hero-copy">
          <p className="eyebrow reveal-line"><span>01 / KUBEILA MATERIALS EXIM</span><span>EST. FOR THE HARD PARTS</span></p>
          <h1><span className="hero-line">Precision</span><span className="hero-line italic">cut in.</span></h1>
          <p className="hero-intro">Carbide inserts and tooling engineered for the moment metal gives way. Explore a sharper standard for CNC & VMC machining.</p>
          <div className="hero-actions">
            <a className="button button-primary" href="#catalog" data-testid="hero-catalog-button">Explore catalogue <ChevronRight size={17}/></a>
            <button className="text-link" onClick={() => setModal(true)} data-testid="hero-quote-button">Request pricing <ArrowUpRight size={16}/></button>
          </div>
        </div>
        <div className="hero-image-wrap">
          <motion.img src={HERO_IMG} alt="Precision CNC milling in a workshop" initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [.22, 1, .36, 1] }}/>
          <div className="image-stamp">KX<br/><small>PRECISION<br/>SERIES / 01</small></div>
        </div>
        <div className="hero-foot"><span>SCROLL TO CUT</span><div className="scroll-line"/><span>28° 36&apos; N · 77° 13&apos; E</span></div>
      </section>

      <div className="marquee" aria-label="Kubeila product quality statement"><div>PRECISION / PERFORMANCE / REPEAT <span>✦</span> PRECISION / PERFORMANCE / REPEAT <span>✦</span> </div></div>

      <section className="manifesto section-pad" id="capabilities">
        <Reveal>
          <div className="section-kicker"><span>02 — THE KX METHOD</span><span>01 / 03</span></div>
          <div className="manifesto-grid">
            <h2>Make every<br/><em>cut</em> count.</h2>
            <div>
              <p className="lead">We source and supply cutting tools for shops that measure twice and still demand more.</p>
              <p className="muted">From the first toolpath to the final surface, Kubeila brings a considered range of inserts, end mills and VMC essentials to your floor — with grades and coatings matched to the job.</p>
              <a className="text-link" href="#contact" data-testid="method-contact-link">Talk to an application specialist <ArrowUpRight size={16}/></a>
            </div>
          </div>
          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-products"><b>500+</b><span>Cutting-edge products</span></div>
            <div className="stat-card" data-testid="stat-clients"><b>120+</b><span>Workshops supplied</span></div>
            <div className="stat-card" data-testid="stat-experience"><b>10+</b><span>Years of tooling know-how</span></div>
            <div className="stat-card" data-testid="stat-support"><b>24h</b><span>Quote turnaround</span></div>
          </div>
        </Reveal>
      </section>

      <section className="catalog section-pad" id="catalog">
        <Reveal>
          <div className="section-kicker"><span>03 — THE CATALOGUE</span><span>{filtered.length.toString().padStart(2, "0")} / {products.length.toString().padStart(2, "0")}</span></div>
          <div className="catalog-head">
            <div>
              <h2>Tools with<br/><em>intent.</em></h2>
              <p className="muted">Popular Kubeila grades, ready to quote.<br/>Need another spec? We’ll find it.</p>
            </div>
            <div className="filters"><Filter size={15}/>{categories.map(c => <button key={c} className={active === c ? "active" : ""} onClick={() => setActive(c)} data-testid={`filter-${c.toLowerCase().replaceAll(" ", "-")}-button`}>{c}</button>)}</div>
          </div>
          <div className="product-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * .08}>
                <ProductCard p={p} addQuote={addQuote} />
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="brands section-pad">
        <Reveal>
          <div className="section-kicker"><span>04 — COMPATIBILITY, CONSIDERED</span><span>POPULAR BRANDS</span></div>
          <div className="brands-row"><span>SANDVIK</span><span>KENNAMETAL</span><span>ISCAR</span><span>KYOCERA</span><span>MITSUBISHI</span></div>
        </Reveal>
      </section>

      <section className="contact section-pad" id="contact">
        <Reveal>
          <div className="contact-panel">
            <div><p className="eyebrow">LET’S MAKE A CLEANER CUT</p><h2>Bring us the<br/><em>hard part.</em></h2></div>
            <div className="contact-side">
              <p>Tell us what you’re machining. We’ll help you land on the right geometry, grade and price.</p>
              <button className="button button-primary" onClick={() => setModal(true)} data-testid="contact-quote-button">Start a quote <Send size={16}/></button>
              <div className="contact-links">
                <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hello Kubeila, I would like to enquire about carbide tooling.")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><MessageCircle size={15}/> WhatsApp — {PHONE_PRIMARY}</a>
                <a href={`mailto:${EMAIL}`} data-testid="contact-email-link"><Mail size={15}/> {EMAIL}</a>
                <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, "")}`} data-testid="contact-phone-link"><Phone size={15}/> {PHONE_PRIMARY} · {PHONE_SECONDARY}</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>

    <footer>
      <Logo compact/>
      <span>© 2025 KUBEILA MATERIALS EXIM / MADE FOR THE HARD PARTS</span>
      <a href="#top" data-testid="back-to-top-link">BACK TO TOP ↑</a>
    </footer>

    <a className="whatsapp-float" href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hello Kubeila, I would like to enquire about carbide tooling.")}`} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp" data-testid="whatsapp-float-button"><MessageCircle size={22}/></a>

    <AnimatePresence>
      {modal && (
        <motion.div className="modal-backdrop" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
          <motion.div className="quote-modal" initial={{y:40, opacity:0}} animate={{y:0, opacity:1}} exit={{y:40, opacity:0}}>
            <button className="close-modal" onClick={() => {setModal(false);setSent("")}} aria-label="Close quote form" data-testid="close-quote-modal"><X/></button>
            <p className="eyebrow">KX / QUOTE DESK</p>
            <h2>Let’s get<br/><em>specific.</em></h2>
            {sent ? (
              <div className="success-message" data-testid="quote-success-message"><Sparkles/><p>{sent}</p><button className="text-link" onClick={() => {setModal(false);setSent("")}}>Close</button></div>
            ) : (
              <form onSubmit={submitQuote} data-testid="quote-form">
                <div className="selected-products">{quote.length ? quote.map(p => <span key={p.id}>{p.name}</span>) : <small>No products selected — tell us what you need.</small>}</div>
                <div className="form-grid">
                  <input name="name" placeholder="Your name *" required data-testid="quote-name-input"/>
                  <input name="company" placeholder="Company" data-testid="quote-company-input"/>
                  <input name="email" type="email" placeholder="Work email *" required data-testid="quote-email-input"/>
                  <input name="phone" placeholder="Phone / WhatsApp" data-testid="quote-phone-input"/>
                </div>
                <textarea name="message" placeholder="Part number, material, volume or any details..." data-testid="quote-message-input"/>
                <button className="button button-primary" type="submit" data-testid="submit-quote-button">Send request <Send size={16}/></button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>;
}

export default Home;
